import type { ConsoleOrganization } from '@sentinel/shared';
import { Button, Panel, StateBadge } from '@sentinel/ui';
import { formatDate } from '@sentinel/web-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsoleCan } from '@/auth/use-console-can';
import { ORGANIZATION_TONE } from './organization-state';
import { StatusChangeModal } from './StatusChangeModal';
import type { StatusChange, useOrganization } from './use-organization';

const K = 'console.organization.status';

type Props = { organization: ConsoleOrganization } & Pick<
  ReturnType<typeof useOrganization>,
  'changeStatus'
>;

/** The changes a state allows, among those the platform role may make. */
function changesOf(
  status: ConsoleOrganization['status'],
  can: { suspend: boolean; archive: boolean },
) {
  const open = status === 'active' || status === 'trial';
  const changes: StatusChange[] = [];
  if (can.suspend && open) changes.push('suspend');
  if (can.suspend && !open) changes.push('reactivate');
  if (can.archive && status !== 'archived') changes.push('archive');
  return changes;
}

/** Suspend (read-only, then console only), reactivate, archive: each confirmed with its
 *  consequences. */
export function StatusPanel({ organization, changeStatus }: Props) {
  const { t } = useTranslation();
  const can = {
    suspend: useConsoleCan('console.organizations:suspend'),
    archive: useConsoleCan('console.organizations:archive'),
  };
  const [confirming, setConfirming] = useState<StatusChange | null>(null);
  const { status } = organization;

  return (
    <Panel title={t(`${K}.title`)}>
      <div className="stack">
        <p style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <StateBadge tone={ORGANIZATION_TONE[status]} label={t(`console.statuses.${status}`)} />
          {organization.suspendedAt === null ? null : (
            <span className="note">
              {t(`${K}.since`, { date: formatDate(organization.suspendedAt) })}
            </span>
          )}
        </p>
        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {changesOf(status, can).map((change) => (
            <Button
              key={change}
              size="sm"
              {...(change === 'reactivate' ? {} : { variant: 'danger' as const })}
              onClick={() => setConfirming(change)}
            >
              {t(`${K}.actions.${change}`)}
            </Button>
          ))}
        </span>
      </div>
      {confirming === null ? null : (
        <StatusChangeModal
          organization={organization}
          change={confirming}
          busy={changeStatus.isPending}
          onClose={() => setConfirming(null)}
          onConfirm={() =>
            changeStatus.mutate(confirming, { onSuccess: () => setConfirming(null) })
          }
        />
      )}
    </Panel>
  );
}
